/**
 * Regras do roteiro.
 *
 * Puras e compartilhadas: rodam idênticas no cliente (cálculo otimista,
 * offline) e no servidor (autoridade). Um desvio entre os dois deixa de ser
 * possível por construção.
 */

import { type CalendarDate, diffCalendarDays, enumerateDays } from './calendar.js';
import type { TripDates } from './trip.js';

export type ActivityStatus = 'planejada' | 'agora' | 'concluida' | 'cancelada';

export interface Activity {
  id: string;
  title: string;
  /** `HH:MM` em 24h. */
  startTime: string;
  /** Fim (PO, 22/09/2026); nulo = só a hora de início. */
  endTime?: string | null;
  /** Sem hora: aparece como "Dia inteiro" no topo do dia. */
  allDay?: boolean;
  locationName?: string;
  /**
   * Lugar estruturado, quando o local veio de uma busca (Places): id do
   * provedor, endereço formatado e coordenadas. `locationName` continua sendo
   * o nome que a pessoa vê; estes campos são o que o mapa e o "Como chegar"
   * usam. Sempre os três de coordenada juntos ou nenhum.
   */
  placeId?: string | null;
  placeAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Nota livre da atividade — a coluna `notes`, não um comentário à parte. */
  notes?: string;
  /**
   * Avisar o grupo N minutos antes do horário (push). Nulo = sem aviso.
   * É da atividade, não da pessoa: quem organiza decide o que merece aviso;
   * cada pessoa decide, no perfil, se recebe avisos neste aparelho.
   */
  alertMinutesBefore?: number | null;
  status: ActivityStatus;
  /** Ordem dentro do dia. É isto que a reordenação muda. */
  sortOrder: number;
  createdBy?: string;
  /**
   * Quem vai (ids de `trip_member`). Vazio ou ausente = todo mundo — é o
   * padrão, e é o que os carimbos e o "quem estava" leem.
   */
  participantMemberIds?: string[];
  /** Foto de capa (id em `media_asset`) e a miniatura assinada que o servidor devolve. */
  coverMediaId?: string | null;
  coverThumbUrl?: string | null;
}

/* ─── Reordenação ──────────────────────────────────────────────────────
 *
 * REGRA CONGELADA (PRD §3.5): a atividade e o horário viajam JUNTOS.
 * Reordenar muda a ordem, nunca reatribui horários às posições.
 *
 *   Antes:  10:30 Guardians · 12:30 Almoço
 *   Mover "Almoço" para cima →
 *   Depois: 12:30 Almoço · 10:30 Guardians
 *
 * A implementação ingênua — reatribuir os horários às novas posições — é
 * silenciosa e destrutiva: ninguém percebe que o horário do almoço mudou até
 * chegar atrasado.
 */

/**
 * Move uma atividade para uma posição.
 *
 * `toIndex` é a posição do SLOT, contada sobre a lista original: 0 antes do
 * primeiro item, `n` depois do último. É como o design apresenta as zonas
 * "SOLTAR AQUI".
 */
export function reorderActivities(
  activities: readonly Activity[],
  activityId: string,
  toIndex: number,
): Activity[] {
  const fromIndex = activities.findIndex((a) => a.id === activityId);
  if (fromIndex === -1) return [...activities];

  const restante = activities.filter((a) => a.id !== activityId);
  const movida = activities[fromIndex] as Activity;

  // Soltar num slot depois da própria posição de origem: o slot "anda" um
  // para trás quando o item sai da lista.
  const destino = Math.max(0, Math.min(restante.length, toIndex > fromIndex ? toIndex - 1 : toIndex));

  restante.splice(destino, 0, movida);

  // Renumera a ordem. O horário de cada atividade permanece o que era —
  // é o ponto inteiro desta função.
  return restante.map((activity, index) => ({ ...activity, sortOrder: (index + 1) * 10 }));
}

/**
 * A ordem manual está fora da ordem cronológica?
 *
 * O sistema permite (PRD §3.5) e não reorganiza sozinho — apenas avisa.
 * Atividades canceladas não contam: elas não vão acontecer. "Dia inteiro"
 * também não (PO, 22/09/2026): sem hora, não há ordem cronológica a violar —
 * ela guarda 00:00 e apareceria sempre como "fora de ordem".
 */
export function isOutOfChronologicalOrder(activities: readonly Activity[]): boolean {
  const horarios = [...activities]
    .filter((a) => a.status !== 'cancelada' && a.allDay !== true)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((a) => a.startTime);

  for (let i = 1; i < horarios.length; i += 1) {
    if ((horarios[i] as string) < (horarios[i - 1] as string)) return true;
  }
  return false;
}

export const CHRONOLOGICAL_WARNING = 'Algumas atividades estão fora da ordem de horário.';

/* ─── Conclusão ───────────────────────────────────────────────────────── */

export interface ToggleResult {
  activity: Activity;
  /** Entrada para o histórico do dia. */
  historyAction: 'concluiu' | 'reabriu';
  toast: string;
}

/**
 * Alterna concluída/reaberta.
 *
 * COMPORTAMENTO UNIFICADO (PRD §14): o `✓` da lista e o botão do detalhe
 * produzem exatamente o mesmo efeito. Por isso existe uma função só — dois
 * caminhos de código seriam duas oportunidades de divergir.
 *
 * Uma atividade cancelada não conclui: não vai acontecer.
 */
export function toggleActivityCompletion(activity: Activity): ToggleResult | null {
  if (activity.status === 'cancelada') return null;

  if (activity.status === 'concluida') {
    return {
      activity: { ...activity, status: 'planejada' },
      historyAction: 'reabriu',
      toast: 'Atividade reaberta · voltou para Planejada',
    };
  }

  return {
    activity: { ...activity, status: 'concluida' },
    historyAction: 'concluiu',
    toast: 'Atividade concluída',
  };
}

/* ─── Dias do roteiro ─────────────────────────────────────────────────── */

export interface ItineraryDaySummary {
  date: CalendarDate;
  /** Base 1, dentro da viagem. */
  dayNumber: number;
  weekday: string;
  isToday: boolean;
}

const DIAS_SEMANA = [
  'DOMINGO', 'SEGUNDA', 'TERÇA', 'QUARTA', 'QUINTA', 'SEXTA', 'SÁBADO',
] as const;

export function weekdayName(date: CalendarDate): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return DIAS_SEMANA[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] as string;
}

/**
 * Todos os dias da viagem, derivados das datas.
 *
 * Nenhuma contagem constante: uma viagem de 3 dias produz 3 linhas, uma de 22
 * produz 22. É a regra de genericidade aplicada ao roteiro.
 */
export function buildItineraryDays(
  trip: TripDates,
  today: CalendarDate | null,
): ItineraryDaySummary[] {
  return enumerateDays(trip.startDate, trip.endDate).map((date) => ({
    date,
    dayNumber: diffCalendarDays(date, trip.startDate) + 1,
    weekday: weekdayName(date),
    isToday: today !== null && date === today,
  }));
}

/* ─── Lugar e "Como chegar" ──────────────────────────────────────────── */

export interface PlaceCoordinates {
  latitude: number;
  longitude: number;
}

/** Coordenadas válidas e presentes as duas — ou nada. */
export function hasCoordinates<T extends Pick<Activity, 'latitude' | 'longitude'>>(
  a: T,
): a is T & PlaceCoordinates {
  return (
    typeof a.latitude === 'number' &&
    typeof a.longitude === 'number' &&
    Number.isFinite(a.latitude) &&
    Number.isFinite(a.longitude) &&
    Math.abs(a.latitude) <= 90 &&
    Math.abs(a.longitude) <= 180
  );
}

/**
 * URL de rota no app de mapas (Google Maps URLs, abre o app nativo quando
 * instalado). Com coordenadas, a rota é exata; com só o nome, é uma busca.
 * Sem nada, não há para onde ir.
 */
export function directionsUrl(
  a: Pick<Activity, 'locationName' | 'placeId' | 'latitude' | 'longitude'>,
): string | null {
  if (hasCoordinates(a)) {
    const destino = `${a.latitude},${a.longitude}`;
    const placeId = a.placeId ? `&destination_place_id=${encodeURIComponent(a.placeId)}` : '';
    return `https://www.google.com/maps/dir/?api=1&destination=${destino}${placeId}`;
  }
  const nome = a.locationName?.trim();
  if (!nome) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(nome)}`;
}

/** Antecedências que o formulário oferece. Rótulo em pt-BR na tela. */
export const ALERT_MINUTES_OPTIONS = [15, 30, 60, 120] as const;

export function isValidAlertMinutes(v: unknown): v is (typeof ALERT_MINUTES_OPTIONS)[number] {
  return typeof v === 'number' && (ALERT_MINUTES_OPTIONS as readonly number[]).includes(v);
}

/**
 * Como o horário de uma atividade é dito na tela (PO, 22/09/2026):
 * "Dia inteiro", "11:00 – 13:00" ou "11:00".
 */
export function horarioDaAtividade(a: { startTime: string; endTime?: string | null; allDay?: boolean }): string {
  if (a.allDay) return 'Dia inteiro';
  return a.endTime ? `${a.startTime} – ${a.endTime}` : a.startTime;
}

/** O fim tem de vir depois do início (mesmo dia). Vazio/nulo é válido. */
export function fimValido(startTime: string, endTime: string | null | undefined): boolean {
  if (!endTime) return true;
  return endTime > startTime;
}

